<script setup lang="ts">
import { computed } from 'vue';
import { manifestCardUses, type Permission } from '@vp/shared-types';
import { catalogState, installState } from '../catalog';
import { t, tm } from '../i18n';
import { confirmPendingInstall, getRuntime } from '../modules';
import Icon from './Icon.vue';
import PermissionList from './PermissionList.vue';
import AccessBridge from './AccessBridge.vue';

/**
 * Installing an official module (TZ 4.3, 5.1): the name, the "Official" badge and the permissions
 * the module asks for — nothing about keys or hashes. One button installs it.
 */
const p = computed(() => installState.pending);
const name = (m: { id: string; name: string | Record<string, string> }) => (typeof m.name === 'string' ? m.name : (tm(m.id, m.name) || m.name['en'] || m.id));
const logo = computed(() => (p.value ? (catalogState.modules.find((m) => m.id === p.value!.manifest.id)?.logo ?? null) : null));
const installed = computed(() => (p.value ? getRuntime()?.get(p.value.manifest.id) : undefined));

function grant(granted: Permission[]): void {
  if (installState.pending) installState.pending = { ...installState.pending, granted: new Set(granted) };
}
</script>

<template>
  <Transition name="pop">
    <div v-if="p" class="dialog-backdrop install-dialog" @pointerdown.self="installState.pending = null" @keydown.esc.stop="installState.pending = null">
      <form class="box" role="dialog" aria-modal="true" :aria-label="t('catalog.installTitle')" @submit.prevent="confirmPendingInstall">
        <AccessBridge :name="name(p.manifest)" :logo="logo" />
        <div class="split">
          <div class="about">
            <p class="meta">
              <span class="ver">{{ p.manifest.version }}</span>
              <span class="badge ok" data-badge="official"><Icon name="check" :size="12" />{{ t('catalog.official') }}</span>
              <span v-if="p.manifest.status" class="badge warn">{{ t('modules.status.' + p.manifest.status) }}</span>
            </p>
            <p class="desc">{{ tm(p.manifest.id, p.manifest.description) }}</p>
            <p v-if="installed" class="note">{{ t('catalog.replaces', { v: installed.version }) }}</p>
          </div>
          <div class="perms">
            <PermissionList v-if="p.manifest.permissions.length || manifestCardUses(p.manifest).length" :requested="p.manifest.permissions" :granted="[...p.granted]" :card-uses="manifestCardUses(p.manifest)" @update="grant" />
            <p v-else class="sub">{{ t('modules.noPerms') }}</p>
          </div>
        </div>
        <div class="actions">
          <button type="button" class="pill" @click="installState.pending = null">{{ t('common.cancel') }}</button>
          <button type="submit" class="pill primary" data-act="confirm-install">{{ installed ? t('catalog.update') : t('catalog.install') }}</button>
        </div>
      </form>
    </div>
  </Transition>
</template>

<style scoped>
.install-dialog {
  z-index: 2900;
  background: var(--backdrop);
}
.box {
  width: min(820px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 22px 22px 16px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  box-shadow: var(--shadow);
}
.split {
  display: flex;
  align-items: flex-start;
  gap: 28px;
  margin-top: 16px;
}
.about {
  flex: 1 1 260px;
  min-width: 0;
}
.perms {
  flex: 1.15 1 340px;
  min-width: 0;
  padding-left: 22px;
  border-left: 1px solid var(--border);
}
@media (max-width: 720px) {
  .split {
    flex-direction: column;
    gap: 16px;
  }
  .perms {
    padding-left: 0;
    border-left: none;
  }
}
.meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
}
.ver {
  font-weight: 400;
  color: var(--text-3);
  font-size: 13px;
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
}
.badge.ok {
  background: var(--ok-soft);
  color: var(--ok);
}
.badge.warn {
  background: var(--warn-soft);
  color: var(--warn);
}
.desc {
  margin: 12px 0 0;
  color: var(--text-2);
  line-height: 1.5;
}
.note {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--text-3);
}
.sub {
  margin: 14px 0 6px;
  font-size: 12px;
  color: var(--text-3);
}
.item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.text {
  flex: 1;
  display: grid;
}
.label {
  font-size: 13px;
}
.hint {
  font-size: 12px;
  color: var(--text-3);
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}
.pill {
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
}
.pill.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.pill.primary:hover {
  background: var(--accent-2);
}
</style>
